import { useState } from "react";
import { Form, notification } from "antd";
import cn from "classnames";
import MDEditor from "@uiw/react-md-editor";

import { useGetTestInstructions } from "@/modules/admin/testManagement/hooks/useGetTestInstructions.hook";

import { Loading } from "@/shared/components/Loading/Loading";
import { TestInstructionsHttpService } from "@/shared/services/tests/test-instructions-http.service";
import { getFriendlyErrorMessage } from "@/shared/services/http";

import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import LocaleContent from "@/ui-components/commonComponents/LocaleContent/LocaleContent";

import { ReactComponent as SaveSVG } from "@/assets/icons/feather-icons/save.svg";

import { DEFAULT_LOCALE, localeLabel, orderLocales } from "@/shared/utils/locale.utils";
import { ITest } from "@/modules/assessments/types/test.interface";

import style from "@/shared/components/assessment/AboutAssessment/style.module.css";
import commonStyle from "../TestSettings/style.module.css";
import questionStyle from "@/shared/components/candidate/scenes/TestElement/style.module.css";

export default function TestInstructions({ test }: { test: ITest }) {
  const { testInstructions, isLoading, applySavedInstructions } = useGetTestInstructions(test.testId);
  const [form] = Form.useForm();
  const [isUpdating, setUpdating] = useState<boolean>(false);

  // One tab per available locale, plus any locale that already has saved instructions; never empty.
  const orderedLocales = orderLocales([...(test.availableLocales ?? []), ...Object.keys(testInstructions ?? {})]);
  const locales = orderedLocales.length > 0 ? orderedLocales : [DEFAULT_LOCALE];

  const onFinish = async () => {
    try {
      setUpdating(true);
      const values: Record<string, string> = form.getFieldsValue(true);
      const existingLocales = new Set(Object.keys(testInstructions ?? {}));
      // Keep a locale that has content, or that already had a saved row so clearing it persists an
      // empty body; skip untouched available locales so they aren't stored as empty rows.
      const body = locales.reduce<Record<string, string>>((acc, locale) => {
        const text = values[locale] ?? "";
        if (text.trim().length > 0 || existingLocales.has(locale)) {
          acc[locale] = text;
        }
        return acc;
      }, {});

      await TestInstructionsHttpService.updateTestInstructionsBody(test.testId, body);
      applySavedInstructions(body);

      notification.success({ message: "Test instructions updated successfully." });
    } catch (err) {
      notification.error({ message: getFriendlyErrorMessage(err) ?? "Failed to update test instructions." });
    } finally {
      setUpdating(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  // Seed the store through initialValues so every pane paints its saved body on the first render.
  // Writing it from an effect instead leaves the editors empty for a frame — the effect runs after
  // the Form mounts — and warns whenever it fires while the loading branch is still rendered.
  const initialValues = locales.reduce<Record<string, string>>((acc, locale) => {
    acc[locale] = testInstructions?.[locale] ?? "";

    return acc;
  }, {});

  return (
    <div className={commonStyle.innerTabsContainer}>
      <Form form={form} name="testInstructions" initialValues={initialValues} onFinish={onFinish} requiredMark={false}>
        <LocaleContent
          locales={locales}
          renderLabel={(locale) => localeLabel(locale)}
          renderPane={(locale) => (
            <Form.Item className={cn(style.inputTitle, style.mdEditor)} name={locale}>
              <MDEditor
                highlightEnable={false}
                height={300}
                textareaProps={{ placeholder: "Type...", "aria-label": `Instructions (${localeLabel(locale)})` }}
                className={questionStyle.questionTextWrapper}
                previewOptions={{
                  components: {
                    input: ({ node, checked, ...props }) => {
                      if (props.type === "radio" || props.type === "checkbox") {
                        return <input {...props} defaultChecked={!!checked} />;
                      }
                      return <input {...props} />;
                    },
                  },
                }}
              />
            </Form.Item>
          )}
        />

        <div style={{ display: "flex", justifyContent: "right", marginTop: 25 }}>
          <SubmitButton icon={<SaveSVG className={style.svgSize} aria-hidden="true" />} isLoading={isUpdating}>
            Save
          </SubmitButton>
        </div>
      </Form>
    </div>
  );
}
